/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SNMPDevice, SNMPDeviceStatus, getSNMPDeviceStatus } from "@/api/snmp";
import { SNMP_REFRESH_INTERVALS } from "@/constants/snmpRefreshIntervals";
import { StatusDot, fmtDuration } from "@/components/servers/kit";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { cn } from "@/lib/utils";

export type SwitchState = "up" | "down" | "idle";

export interface SwitchInfo {
  state: SwitchState;
  label: "online" | "unreachable" | "disabled" | "checking";
  status?: SNMPDeviceStatus;
  /** the poll has not answered yet */
  loading: boolean;
}

function deriveInfo(device: SNMPDevice, status: SNMPDeviceStatus | undefined, loading: boolean): SwitchInfo {
  if (!device.enabled) return { state: "idle", label: "disabled", status, loading: false };
  if (loading && !status) return { state: "idle", label: "checking", status, loading: true };
  if (status?.reachable) return { state: "up", label: "online", status, loading: false };
  return { state: "down", label: "unreachable", status, loading: false };
}

const statusQuery = (device: SNMPDevice) => ({
  queryKey: ["snmp-device-status", device.id],
  queryFn: () => getSNMPDeviceStatus(device.id),
  refetchInterval: device.enabled ? SNMP_REFRESH_INTERVALS.STATUS : (false as const),
  enabled: device.enabled,
});

/** Live status of one switch (same cache as everywhere else, so no extra polling). */
export function useSwitchInfo(device: SNMPDevice): SwitchInfo {
  const { data, isLoading } = useQuery(statusQuery(device));
  return deriveInfo(device, data, isLoading);
}

/** Status of every switch, in the order given. */
export function useSwitchInfos(devices: SNMPDevice[]): SwitchInfo[] {
  const results = useQueries({ queries: devices.map(statusQuery) });
  return devices.map((d, i) => deriveInfo(d, results[i]?.data, !!results[i]?.isLoading));
}

export const uptimeText = (status?: SNMPDeviceStatus): string =>
  status?.reachable && status.sysUpTimeTicks ? fmtDuration(status.sysUpTimeTicks / 100) : "—";

/** First line of sysDescr is usually the model / OS: "Cisco IOS Software, C2960…". */
export const modelText = (status?: SNMPDeviceStatus): string =>
  status?.sysDescr ? status.sysDescr.split(/\r?\n/)[0].trim() : "";

export const StatusPill: React.FC<{ info: SwitchInfo }> = ({ info }) => (
  <span className={cn("sv-pill", info.state === "up" && "sv-pill--up", info.state === "down" && "sv-pill--down")}>
    <StatusDot state={info.state} pulse={info.loading} />
    {info.label}
  </span>
);

/** Fleet summary: how many are up, how many ports there are in total. */
export const SwitchSummary: React.FC<{ devices: SNMPDevice[]; infos: SwitchInfo[] }> = ({ devices, infos }) => {
  const up = infos.filter((i) => i.state === "up").length;
  const down = infos.filter((i) => i.label === "unreachable").length;
  const off = infos.filter((i) => i.label === "disabled").length;
  const ports = infos.reduce((n, i) => n + (i.state === "up" ? (i.status?.interfaceCount ?? 0) : 0), 0);
  return (
    <div className="sw-sum">
      <div className={cn("sw-kpi", down === 0 && up > 0 && "sw-kpi--ok")}>
        <small>Online</small>
        <b>
          <AnimatedNumber value={up} mountFrom={null} />
          <i>{` / ${devices.length}`}</i>
        </b>
      </div>
      <div className={cn("sw-kpi", down > 0 && "sw-kpi--crit")}>
        <small>Unreachable</small>
        <b>
          <AnimatedNumber value={down} mountFrom={null} />
        </b>
      </div>
      <div className="sw-kpi">
        <small>Disabled</small>
        <b>
          <AnimatedNumber value={off} mountFrom={null} />
        </b>
      </div>
      <div className="sw-kpi">
        <small>Ports total</small>
        <b>
          <AnimatedNumber value={ports} mountFrom={null} />
        </b>
      </div>
    </div>
  );
};

interface ViewProps {
  devices: SNMPDevice[];
  infos: SwitchInfo[];
  selectedId?: number;
  onSelect: (d: SNMPDevice) => void;
  onEdit: (d: SNMPDevice) => void;
  onDelete: (d: SNMPDevice) => void;
}

const rank = (i: SwitchInfo) => (i.label === "unreachable" ? 0 : i.label === "checking" ? 1 : i.label === "online" ? 2 : 3);

/** Worst first, then by name, so a problem is never buried. */
export function sortSwitches(devices: SNMPDevice[], infos: SwitchInfo[]): { device: SNMPDevice; info: SwitchInfo }[] {
  return devices
    .map((device, i) => ({ device, info: infos[i] }))
    .sort((a, b) => rank(a.info) - rank(b.info) || a.device.name.localeCompare(b.device.name));
}

export const SwitchTable: React.FC<ViewProps> = ({ devices, infos, selectedId, onSelect, onEdit, onDelete }) => (
  <div className="sw-table" role="table" aria-label="Switches">
    <div className="sw-trow sw-thead" role="row">
      <span>Switch</span>
      <span>Status</span>
      <span className="sw-num">Ports</span>
      <span className="sw-num">Uptime</span>
      <span className="sw-model">Model</span>
      <span />
    </div>
    {sortSwitches(devices, infos).map(({ device: d, info }) => (
      <div
        key={d.id}
        role="row"
        tabIndex={0}
        className={cn("sw-trow", selectedId === d.id && "sw-trow--sel")}
        onClick={() => onSelect(d)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(d))}
      >
        <span className="sw-name">
          <StatusDot state={info.state} pulse={info.loading} />
          <span>
            <b>{d.name}</b>
            <small>{`${d.host}:${d.port} · SNMPv${d.version}`}</small>
          </span>
        </span>
        <StatusPill info={info} />
        <span className="sw-num">{info.state === "up" && info.status?.interfaceCount !== undefined ? info.status.interfaceCount : "—"}</span>
        <span className="sw-num">{uptimeText(info.status)}</span>
        <span className="sw-model" title={modelText(info.status)}>
          {modelText(info.status) || "—"}
        </span>
        <span className="sw-act" onClick={(e) => e.stopPropagation()}>
          <button type="button" aria-label={`Edit ${d.name}`} title="Edit" onClick={() => onEdit(d)}>
            <PencilIcon />
          </button>
          <button type="button" className="sw-act--del" aria-label={`Remove ${d.name}`} title="Remove" onClick={() => onDelete(d)}>
            <TrashIcon />
          </button>
        </span>
      </div>
    ))}
  </div>
);

export const SwitchCards: React.FC<ViewProps> = ({ devices, infos, selectedId, onSelect }) => (
  <div className="sv-fleet">
    {sortSwitches(devices, infos).map(({ device: d, info }) => (
      <button
        key={d.id}
        type="button"
        className={cn("sv-card", `sv-card--${info.state}`, selectedId === d.id && "sv-card--sel")}
        onClick={() => onSelect(d)}
        title={info.label}
      >
        <span className="sv-card-accent" aria-hidden="true" />
        <div className="sv-card-top">
          <StatusDot state={info.state} pulse={info.loading} />
          <span className="sv-card-name">{d.name}</span>
          <span className="sv-card-up">{info.state === "up" ? uptimeText(info.status) : info.label}</span>
        </div>
        <span className="sv-card-host">{`${d.host}:${d.port}`}</span>
        <div className="sw-card-rows">
          <div>
            <small>Ports</small>
            <b>{info.state === "up" && info.status?.interfaceCount !== undefined ? info.status.interfaceCount : "—"}</b>
          </div>
          <div>
            <small>SNMP</small>
            <b>{`v${d.version} · ${d.pollIntervalSeconds}s`}</b>
          </div>
        </div>
        <span className="sw-card-model" title={modelText(info.status)}>
          {modelText(info.status) || (info.state === "down" ? "no answer to SNMP" : " ")}
        </span>
      </button>
    ))}
  </div>
);
